import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import BFConfirmButton from "@/modules/abstract-ui/general/Button/BFConfirmButton";
import InvoiceStruct from "@/redux/actions/struct/implemented/InvoiceStruct";
import PermissionService from "@/services/PermissionService";
import ArrayUtils from "@/utils/ArrayUtils";
import { Trans } from "react-i18next";
import { HistoryEntryObject } from "../../../../../components/HistoryViewer/IHistoryEntry";
import i18n from "../../../../../i18n";
import {
  CompleteInvoiceFollowUpTaskAsset,
  extractTaskAssignees,
  TaskAsset,
  TaskType,
} from "../../../../../model/general-assets/TaskAsset";
import BFCheckbox from "../../../../../modules/abstract-ui/forms/checkbox/BFCheckbox";
import LanguageService from "../../../../../services/LanguageService";
import InvoiceService from "../../InvoiceService";
import { Invoice, InvoiceKind, InvoiceTask } from "../../RAInterfaces";
import { evaluateApprovalChain } from "../../RAUtils";
import { openInvoiceBooking } from "../BookInvoices/RABookInvoiceModal";
import InvoicePaymentPlan from "../ListInvoices/InvoicePaymentPlan";

export type PastProcessEntryType = {
  type: "past";
  statusText: string;
  user: string;
  date: Date;
  subText?: string;
  comment?: string;
  renderExtra?: () => React.ReactNode;
};
export type CurrentProcessEntryType = {
  type: "current";
  statusText: string;
  assignee?: { users: string[]; teams: string[] };
  renderExtra?: () => React.ReactNode;
  icon: string;
};
export type FutureProcessEntryType = {
  type: "future";
  statusText: string;
  assignee?: { users: string[]; teams: string[] };
  renderExtra?: () => React.ReactNode;
  icon: string;
};
export type ProcessEntryType =
  | PastProcessEntryType
  | CurrentProcessEntryType
  | FutureProcessEntryType;

export const FutureProcessTypeFormatter = (
  invoice: Invoice,
  task?: TaskAsset
) => {
  const output: FutureProcessEntryType[] = [];

  if (invoice.data.status !== "declined") {
    const futureApprovals = evaluateApprovalChain(invoice);

    futureApprovals.forEach((approval) => {
      if (approval) {
        output.push({
          type: "future",
          icon: "cursor-choose-1",
          statusText: i18n.t(
            "ra:Approval.history.future_approval",
            "Ausstehend"
          ),
          assignee: approval,
        });
      }
    });
  }

  if (
    !invoice?.data.currentTask?.includes(InvoiceTask.BOOKING) &&
    !invoice?.data.booked
  ) {
    output.push({
      type: "future",
      icon: "accounting-invoice",
      statusText: i18n.t(
        "ra:Approval.history.future_accounting",
        "In Buchhaltung"
      ),
    });
  }
  const modeOfPayment = InvoiceStruct.getModeOfPayment(
    invoice?.data.direction,
    invoice?.data.invoice?.paymentType
  );

  if (invoice?.data.status !== "declined" && modeOfPayment?.kind !== "sepa") {
    if (
      !invoice?.data.currentTask?.includes(InvoiceTask.PAYING) &&
      !invoice?.data.paid
    ) {
      output.push({
        type: "future",
        icon: "credit-card-give",
        statusText: i18n.t("ra:Approval.history.future_payment", "In Zahlung"),
      });
    }
  }

  if (invoice.data.status !== "declined") {
    if (!invoice?.data.paid) {
      output.push({
        type: "future",
        icon: "check-circle",
        statusText: i18n.t(
          "ra:Approval.history.future_payment_done",
          "Bezahlt"
        ),
      });
    }
  }
  return ArrayUtils.reverse(output);
};

export const CurrentProcessTypeFormatter = (
  invoice: Invoice,
  task?: TaskAsset
) => {
  const output: CurrentProcessEntryType[] = [];

  if (invoice?.data.currentTask?.includes(InvoiceTask.BOOKING)) {
    output.push({
      type: "current",
      icon: "accounting-invoice",
      statusText: i18n.t("ra:Approval.history.current_booking"),
      renderExtra: () => (
        <BFButton
          appearance="link"
          onClick={() => {
            openInvoiceBooking(invoice?._id, () => {});
          }}
          noPadding
          disabled={
            !PermissionService.hasBusinessUnitRole("inv_accounting")?.includes(
              invoice?.data.type
            )
          }
          className={`margin-top-10`}
        >
          {i18n.t("ra:Approval.history.current_booking_open", "Zur Buchung")}
        </BFButton>
      ),
    });
  }

  if (
    invoice?.data.kind === InvoiceKind.INVOICE &&
    invoice?.data.currentTask?.includes(InvoiceTask.PAYING)
  ) {
    output.push({
      type: "current",
      icon: "credit-card-give",
      statusText: i18n.t("ra:Approval.history.current_payment"),
      renderExtra: () => (
        <div className={`current-payment-entries`}>
          <InvoicePaymentPlan size="sm" asset={invoice} type="payment" />
          <InvoicePaymentPlan size="sm" asset={invoice} type="installment" />
        </div>
      ),
    });
  }
  if (task) {
    if (
      (task.data.taskType === TaskType.APPROVE_INCOMING_INVOICE ||
        task.data.taskType === TaskType.APPROVE_OUTGOING_INVOICE) &&
      (task.data.restrictTo.teams.length !== 0 ||
        task.data.restrictTo.users.length !== 0)
    ) {
      output.push({
        type: "current",
        icon: "cursor-choose-1",
        statusText: i18n.t("ra:Approval.history.current_approval"),
        assignee: extractTaskAssignees(task),
      });
    }
    if (task.data.taskType === "COMPLETE_INVOICE_FOLLOW_UP") {
      const followUpTask = task as CompleteInvoiceFollowUpTaskAsset;
      output.push({
        type: "current",
        icon: "common-file-text-subtract",
        statusText: i18n.t("ra:Approval.history.current_followup"),
        assignee: extractTaskAssignees(task),
        renderExtra: () => {
          return (
            <div>
              {followUpTask.data.metaInfos.needStorno && (
                <BFCheckbox
                  checked={!!followUpTask.data.metaInfos.linkedStorno}
                  readOnly
                >
                  {i18n.t("ra:FollowUp.StornoNeeded")}
                </BFCheckbox>
              )}
              {followUpTask.data.metaInfos.needInvoice && (
                <BFCheckbox
                  checked={!!followUpTask.data.metaInfos.linkedInvoice}
                  readOnly
                >
                  {i18n.t("ra:FollowUp.InvoiceNeeded")}
                </BFCheckbox>
              )}
            </div>
          );
        },
      });
    }
  }
  return output;
};

export const PastProcessTypeFormatter = (
  historyEntry: HistoryEntryObject,
  invoice: Invoice,
  index: number
) => {
  if (
    historyEntry.type === "status_update" &&
    historyEntry.data?.status === "inApproval"
  ) {
    return {
      type: "past",
      statusText: i18n.t("ra:Approval.history.past_startApproval"),
      user: historyEntry.user,
      date: historyEntry.date,
    } as PastProcessEntryType;
  } else if (historyEntry.type === "change_chain") {
    return {
      type: "past",
      statusText: i18n.t("ra:Approval.history.past_change_chain"),
      user: historyEntry.user,
      date: historyEntry.date,
      renderExtra: () => {
        const from =
          LanguageService.translateLabel(
            InvoiceStruct.getInvoiceType(historyEntry.data.from)?.data
              .displayName
          ) || "-";
        const to =
          LanguageService.translateLabel(
            InvoiceStruct.getInvoiceType(historyEntry.data.to)?.data.displayName
          ) || "-";
        return (
          <span className={`invoice-change`}>
            <Trans i18nKey="ra:Approval.history.past_change_chain_extra">
              Von <strong>{{ from }}</strong> zu <strong>{{ to }}</strong>
            </Trans>
          </span>
        );
      },
    } as PastProcessEntryType;
  } else if (historyEntry.type === "invoice_payment") {
    return {
      type: "past",
      statusText: i18n.t(
        "ra:Approval.history.invoice_payment",
        "Wurde überwiesen"
      ),
      user: historyEntry.user,
      date: historyEntry.date,
      comment: historyEntry.data?.comment,
      // renderExtra: () => (
      //   <div className={`current-payment-entries`}>
      //     <InvoicePaymentPlan size="sm" asset={invoice} type="payment" />
      //     <InvoicePaymentPlan size="sm" asset={invoice} type="installment" />
      //   </div>
      // ),
    } as PastProcessEntryType;
  } else if (historyEntry.type === "invoice_approve") {
    return {
      type: "past",
      statusText: i18n.t("ra:Approval.history.past_approved"),
      user: historyEntry.user,
      date: historyEntry.date,
      comment: historyEntry.data?.comment,
    } as PastProcessEntryType;
  } else if (historyEntry.type === "invoice_decline") {
    return {
      type: "past",
      statusText: i18n.t("ra:Approval.history.past_declined"),
      user: historyEntry.user,
      date: historyEntry.date,
      subText: LanguageService.translateLabel(
        InvoiceStruct.getDeclineReason(
          invoice?.data.direction,
          historyEntry.data.reason
        )?.displayName
      ),
      comment: historyEntry.data?.comment,
    } as PastProcessEntryType;
  } else if (historyEntry.type === "invoice_approve_override") {
    return {
      type: "past",
      statusText: i18n.t(
        "ra:Approval.history.past_approve_override",
        "Genehmigt in Vertretung"
      ),
      user: historyEntry.user,
      date: historyEntry.date,
      comment: historyEntry.data?.comment,
    } as PastProcessEntryType;
  } else if (historyEntry.type === "invoice_booked") {
    return {
      type: "past",
      statusText: i18n.t("ra:Approval.history.past_booked"),
      user: historyEntry.user,
      date: historyEntry.date,
      // check if this is the last entry of all history entries of type invoice_booked, if so, show the revert booking button
      renderExtra: () => {
        const isLast =
          invoice.data.history.findLastIndex(
            (e) => e.type === "invoice_booked"
          ) === index;
        // if (!isLast) {
        //   return undefined;
        // }
        return PermissionService.hasBusinessUnitRole(
          "inv_accounting"
        )?.includes(invoice?.data.type) && invoice.data.booked ? (
          <BFConfirmButton
            appearance="link"
            onConfirm={async () => {
              await InvoiceService.revertBooking(invoice?._id);
            }}
            confirmButtonText={i18n.t(
              "ra:Approval.history.revertBooking",
              "Buchung rückgängig machen"
            )}
            noPadding
          >
            {i18n.t(
              "ra:Approval.history.revertBooking",
              "Buchung rückgängig machen"
            )}
          </BFConfirmButton>
        ) : undefined;
      },
    } as PastProcessEntryType;
  } else if (historyEntry.type === "invoice_payed") {
    return {
      type: "past",
      statusText: i18n.t("ra:Approval.history.past_payed"),
      user: historyEntry.user,
      date: historyEntry.date,
    } as PastProcessEntryType;
  } else if (historyEntry.type === "invoice_checked") {
    return {
      type: "past",
      statusText: i18n.t("ra:Approval.history.past_checked_true"),
      user: historyEntry.user,
      date: historyEntry.date,
    } as PastProcessEntryType;
  } else if (historyEntry.type === "invoice_follow_up") {
    return {
      type: "past",
      statusText: i18n.t("ra:Approval.history.past_followup"),
      user: historyEntry.user,
      date: historyEntry.date,
    } as PastProcessEntryType;
  }
  return null;
};
